import { ArrowUpDown, Check, Clock, Inbox, ListFilter, Mail, MailOpen, Search, Star, Trash2 } from "lucide-react";
import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, Spinner } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { Avatar, Tooltip } from "@/components/ui/misc";
import {
  ACTIVITY_TYPES, activityPreview, activityType, conversationTitle, INBOX_TABS, latestActivity, SORTS,
  type ActivityType, type Conversation, type InboxSort, type InboxTab,
} from "@/lib/conversations";
import { cn } from "@/lib/utils";
import { TypeMark } from "@/features/conversations/channel-ui";

export type BulkAction = "read" | "unread" | "star" | "delete";

/** Where the list stands in the inbox: whether there is more, and how to ask for it. */
export interface ListPage {
  /** How many pages are in. Each one landing is another chance to ask for the next. */
  pages: number;
  more: boolean;
  loading: boolean;
  /** The last ask for the next page failed: it is asked again from Retry, not by scrolling. */
  failed: boolean;
  onMore: () => void;
}

/** How close to the list's end, in pixels, the next page is asked for. */
const MORE_AT_PX = 240;

const TAB_ICONS: Record<InboxTab, typeof Inbox> = {
  unread: MailOpen,
  all: Inbox,
  recent: Clock,
  starred: Star,
};

/**
 * The inbox's left pane: which inbox, the four tabs (Unread, All, Recent, Starred), search, sort,
 * a filter by what happened last, and the threads. A row says who, what happened last and when,
 * on which channel, how much of it is unread, and whether it is starred.
 *
 * `organize` offers what only the page keeps — star, the Starred tab, delete. Off for core-api's
 * conversations, which keep none of them yet (`ORGANIZING_KEPT`): read and unread remain.
 */
export function InboxList({
  title, conversations, unread, tab, onTab, search, onSearch, sort, onSort, types, onTypes,
  openId, onOpen, onStar, onBulk, organize = true, page, className,
}: {
  title: string;
  conversations: Conversation[];
  /** Unread threads in this inbox, for the Unread tab's badge. */
  unread: number;
  tab: InboxTab;
  onTab: (tab: InboxTab) => void;
  search: string;
  onSearch: (search: string) => void;
  sort: InboxSort;
  onSort: (sort: InboxSort) => void;
  types: ActivityType[];
  onTypes: (types: ActivityType[]) => void;
  openId: string | null;
  onOpen: (id: string) => void;
  onStar: (id: string, starred: boolean) => void;
  /** An action on every selected thread. */
  onBulk: (ids: string[], action: BulkAction) => void;
  organize?: boolean;
  /** The inbox's paging: the next page loads as the list nears its end. */
  page?: ListPage;
  className?: string;
}) {
  const tabs = INBOX_TABS.filter((t) => organize || t.tab !== "starred");
  const scroller = React.useRef<HTMLDivElement>(null);
  const end = React.useRef<HTMLDivElement>(null);
  // The next page is asked for when the end of the list comes within `MORE_AT_PX` of the view: by
  // scrolling, by the keyboard, or at once for a list too short to scroll. One ask per observer, and
  // a new one once that page is in (`pages`: a page can land without a render that shows it
  // loading), so nothing is asked for twice.
  const want = !!page?.more && !page.loading && !page.failed;
  const onMore = React.useRef(page?.onMore);
  onMore.current = page?.onMore;
  React.useEffect(() => {
    const root = scroller.current;
    const target = end.current;
    if (!want || !root || !target || typeof IntersectionObserver === "undefined") return;
    let asked = false;
    const watch = new IntersectionObserver(
      (entries) => {
        if (asked || !entries.some((e) => e.isIntersecting)) return;
        asked = true;
        onMore.current?.();
      },
      { root, rootMargin: `0px 0px ${MORE_AT_PX}px 0px` },
    );
    watch.observe(target);
    return () => watch.disconnect();
  }, [want, page?.pages]);
  // Another tab, search, sort or filter is another list: it starts from its top.
  React.useEffect(() => {
    scroller.current?.scrollTo?.({ top: 0 });
  }, [tab, search, sort, types]);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [deleting, setDeleting] = React.useState(false);
  // A selection is of what is on screen: a thread the view no longer shows drops out of it.
  const shownIds = conversations.map((c) => c.id);
  const picked = shownIds.filter((id) => selected.has(id));
  const all = picked.length > 0 && picked.length === shownIds.length;
  const bulk = (action: BulkAction) => {
    onBulk(picked, action);
    setSelected(new Set());
  };
  const toggle = (id: string, on: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  return (
    <div className={cn("flex w-full min-w-0 flex-col border-border lg:w-80 lg:shrink-0 lg:border-r", className)}>
      <div className="flex items-center gap-1 px-3 pt-3">
        <h2 className="min-w-0 flex-1 truncate text-base font-semibold text-text">{title}</h2>
        <FilterMenu types={types} onTypes={onTypes} />
        <SortMenu sort={sort} onSort={onSort} />
      </div>

      <div
        className={cn("grid border-b border-border px-1 pt-2", tabs.length === 4 ? "grid-cols-4" : "grid-cols-3")}
        role="tablist"
        aria-label="Show"
      >
        {tabs.map((t) => {
          const Icon = TAB_ICONS[t.tab];
          const on = tab === t.tab;
          return (
            <button
              key={t.tab}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => onTab(t.tab)}
              className={cn(
                "relative flex flex-col items-center gap-0.5 border-b-2 px-1 pb-2 pt-1 text-xs font-medium transition-colors",
                on ? "border-accent-9 text-text" : "border-transparent text-text-muted hover:text-text",
              )}
            >
              <span className="relative">
                <Icon className="size-4" aria-hidden />
                {t.tab === "unread" && unread > 0 && (
                  <span className="absolute -right-3 -top-2 rounded-full bg-accent-9 px-1 text-[10px] font-semibold leading-4 text-white">
                    {unread > 999 ? `${(unread / 1000).toFixed(1)}K` : unread}
                  </span>
                )}
              </span>
              {t.label}
            </button>
          );
        })}
      </div>

      <div className="border-b border-border p-2">
        <Input
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search name, number, email or message"
          leading={<Search />}
          aria-label="Search conversations"
        />
      </div>

      {conversations.length > 0 && (
        <div className="flex h-9 items-center gap-2 border-b border-border px-3 text-xs">
          <label className="flex items-center gap-2 text-text-muted">
            <input
              type="checkbox"
              className="size-3.5 accent-[var(--color-accent-9)]"
              checked={all}
              ref={(el) => {
                if (el) el.indeterminate = picked.length > 0 && !all;
              }}
              onChange={(e) => setSelected(e.target.checked ? new Set(shownIds) : new Set())}
            />
            {picked.length > 0 ? `${picked.length} selected` : "Select all"}
          </label>
          {picked.length > 0 && (
            <div className="ml-auto flex items-center gap-0.5" role="group" aria-label="With the selected">
              <Tooltip content="Mark as read">
                <Button variant="ghost" size="icon-sm" aria-label="Mark selected as read" onClick={() => bulk("read")}>
                  <MailOpen />
                </Button>
              </Tooltip>
              <Tooltip content="Mark as unread">
                <Button variant="ghost" size="icon-sm" aria-label="Mark selected as unread" onClick={() => bulk("unread")}>
                  <Mail />
                </Button>
              </Tooltip>
              {organize && (
                <>
                  <Tooltip content="Star">
                    <Button variant="ghost" size="icon-sm" aria-label="Star selected" onClick={() => bulk("star")}>
                      <Star />
                    </Button>
                  </Tooltip>
                  <Tooltip content="Delete">
                    <Button variant="ghost" size="icon-sm" aria-label="Delete selected" onClick={() => setDeleting(true)}>
                      <Trash2 />
                    </Button>
                  </Tooltip>
                </>
              )}
            </div>
          )}
          <ConfirmDialog
            open={deleting}
            onOpenChange={setDeleting}
            title={`Delete ${picked.length} conversation${picked.length === 1 ? "" : "s"}?`}
            description="They leave every inbox. Their messages, calls and recordings are kept on the contacts' records."
            confirmLabel="Delete"
            destructive
            onConfirm={() => {
              setDeleting(false);
              bulk("delete");
            }}
          />
        </div>
      )}

      {conversations.length === 0 && !page?.more ? (
        <EmptyState
          className="flex-1"
          title={search || types.length ? "No conversations match" : emptyTitle(tab)}
          description={
            search || types.length ? "Try another search, or clear the filter." : "Nothing in this view right now."
          }
          action={
            types.length > 0 ? (
              <Button variant="outline" size="sm" onClick={() => onTypes([])}>
                Clear filter
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto scrollbar-subtle">
          <ul aria-label="Conversations" aria-busy={page?.loading || undefined}>
            {conversations.map((c) => (
              <li key={c.id}>
                <ConversationRow
                  conversation={c}
                  checked={selected.has(c.id)}
                  onCheck={(on) => toggle(c.id, on)}
                  selected={c.id === openId}
                  onOpen={() => onOpen(c.id)}
                  onStar={organize ? () => onStar(c.id, !c.starred) : undefined}
                />
              </li>
            ))}
          </ul>
          {page?.loading ? (
            <div className="flex items-center justify-center gap-2 px-3 py-3 text-xs text-text-muted" role="status">
              <Spinner className="size-4" />
              Loading more conversations
            </div>
          ) : page?.failed ? (
            <div className="flex items-center justify-center gap-2 px-3 py-2 text-xs text-text-muted" role="alert">
              Could not load more conversations.
              <Button variant="ghost" size="sm" onClick={page.onMore}>
                Retry
              </Button>
            </div>
          ) : null}
          <div ref={end} aria-hidden className="h-px" />
        </div>
      )}
    </div>
  );
}

function emptyTitle(tab: InboxTab): string {
  return tab === "unread" ? "You're all caught up" : tab === "starred" ? "No starred conversations" : tab === "recent" ? "Nothing in the last day" : "No conversations";
}

function FilterMenu({ types, onTypes }: { types: ActivityType[]; onTypes: (types: ActivityType[]) => void }) {
  return (
    <DropdownMenu>
      <Tooltip content="Filter">
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label="Filter conversations" className={cn(types.length > 0 && "text-accent-11")}>
            <ListFilter />
          </Button>
        </DropdownMenuTrigger>
      </Tooltip>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Latest activity</DropdownMenuLabel>
        {ACTIVITY_TYPES.map((t) => (
          <DropdownMenuCheckboxItem
            key={t.type}
            checked={types.includes(t.type)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(on) =>
              onTypes(on ? [...types, t.type] : types.filter((x) => x !== t.type))
            }
          >
            {t.label}
          </DropdownMenuCheckboxItem>
        ))}
        {types.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => onTypes([])}>Clear filter</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function SortMenu({ sort, onSort }: { sort: InboxSort; onSort: (sort: InboxSort) => void }) {
  return (
    <DropdownMenu>
      <Tooltip content="Sort">
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label="Sort conversations">
            <ArrowUpDown />
          </Button>
        </DropdownMenuTrigger>
      </Tooltip>
      <DropdownMenuContent align="end">
        {SORTS.map((s) => (
          <DropdownMenuItem key={s.sort} onSelect={() => onSort(s.sort)}>
            <Check className={cn(s.sort !== sort && "invisible")} />
            {s.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** "3:51 PM" today, "Mon" this week, "Sep 12" before that. */
function rowTime(iso: string | undefined): string {
  if (!iso) return "";
  const at = new Date(iso);
  const now = new Date();
  if (at.toDateString() === now.toDateString()) {
    return at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  if (now.getTime() - at.getTime() < 6 * 24 * 60 * 60 * 1000) {
    return at.toLocaleDateString(undefined, { weekday: "short" });
  }
  return at.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function ConversationRow({
  conversation: c, checked, onCheck, selected, onOpen, onStar,
}: {
  conversation: Conversation;
  checked: boolean;
  onCheck: (on: boolean) => void;
  selected: boolean;
  onOpen: () => void;
  /** Absent where a star is not kept. */
  onStar?: () => void;
}) {
  const last = latestActivity(c);
  const title = conversationTitle(c);
  const unread = c.unread > 0;
  return (
    <div
      className={cn(
        "group relative flex items-start gap-2.5 border-b border-border px-3 py-2.5 transition-colors",
        selected ? "bg-accent-2 before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:bg-accent-9" : "hover:bg-surface-hover",
      )}
    >
      <input
        type="checkbox"
        className="mt-3 size-3.5 shrink-0 accent-[var(--color-accent-9)]"
        checked={checked}
        onChange={(e) => onCheck(e.target.checked)}
        aria-label={`Select ${title}`}
      />
      <button
        type="button"
        onClick={onOpen}
        aria-current={selected || undefined}
        className="flex min-w-0 flex-1 items-start gap-2.5 text-left outline-none"
      >
        <span className="relative mt-0.5">
          <Avatar name={c.contactName} className="size-8" />
          <TypeMark type={activityType(last)} className="absolute -bottom-1 -right-1" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className={cn("min-w-0 flex-1 truncate text-sm", unread ? "font-semibold text-text" : "text-text")}>
              {title}
            </span>
            <time className="shrink-0 text-2xs text-text-subtle" dateTime={last?.at}>
              {rowTime(last?.at)}
            </time>
          </span>
          <span className="mt-0.5 flex items-center gap-2">
            <span className={cn("min-w-0 flex-1 truncate text-xs", unread ? "text-text" : "text-text-muted")}>
              {activityPreview(last)}
            </span>
            {unread && (
              <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded bg-accent-9 px-1 text-2xs font-semibold text-white">
                <span className="sr-only">Unread: </span>
                {c.unread}
              </span>
            )}
          </span>
        </span>
      </button>
      {onStar && (
        <button
          type="button"
          onClick={onStar}
          aria-pressed={c.starred}
          aria-label={c.starred ? `Unstar ${title}` : `Star ${title}`}
          className={cn(
            "mt-6 rounded p-0.5 transition-colors hover:bg-surface-hover",
            c.starred ? "text-warning-9" : "text-text-subtle opacity-60 hover:opacity-100",
          )}
        >
          <Star className={cn("size-3.5", c.starred && "fill-current")} aria-hidden />
        </button>
      )}
    </div>
  );
}
